import type { KeyboardEvent } from "react";

import type { CustomerView, InvoiceTarget } from "../lib/api";
import { t, tn, type Language } from "../lib/i18n";

interface KpiTilesProps {
  view: CustomerView | null;
  language: Language;
  /** Localized active-range label for the aria-label (e.g. "Este ano").
   * Defaults to the static `app.period` string when the dashboard has no
   * range picker wired (standalone / older callers). */
  periodLabel?: string;
  /** Drill into the Invoices tab pre-filtered. Optional so the tiles render
   * non-interactive in isolation (standalone / unit tests). */
  onTile?: (target: InvoiceTarget) => void;
}

interface Tile {
  key: string;
  label: string;
  value: string;
  target: InvoiceTarget;
  /** Amber-highlight the tile (e.g. "items to review" when > 0). */
  highlight?: boolean;
}

/** Landing-view KPI tiles (F37 §12.4, Task 4) — trimmed to the THREE
 * headline figures of verifiable agent work (processed · filed · to-review);
 * the softer / estimated figures (hours saved, vendors discovered) drop to a
 * muted secondary line so the tiles stay honest. Each tile is a button that
 * drills into the Invoices list pre-filtered. Data-layer squares (0–2px
 * corners). All values are window-local; the window is labelled with the
 * localized range label (NOT the backend's raw `period_label` token). */
export function KpiTiles({
  view,
  language,
  periodLabel,
  onTile,
}: KpiTilesProps): JSX.Element {
  const windowLabel =
    t("kpi.period_label", language) +
    ": " +
    (periodLabel ?? t("app.period", language));

  if (!view) {
    return (
      <section
        className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        aria-busy="true"
      >
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-28 animate-pulse rounded-[2px] bg-kwanza-divider"
            data-testid="kpi-tile-skeleton"
          />
        ))}
      </section>
    );
  }

  // First-run / empty-window state — a window the agent did nothing in must
  // not read as a wall of zeros / "broken" (F37 §12.3). "items to review = 0"
  // is the healthy norm and does not, by itself, mean "no data".
  const isEmpty =
    view.invoices_processed === 0 &&
    view.invoices_filed_count === 0 &&
    view.hours_saved === 0 &&
    view.exceptions_raised_count === 0 &&
    view.new_vendors_discovered.length === 0;

  if (isEmpty) {
    return (
      <section
        className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-6 text-center shadow-sm"
        aria-label={windowLabel}
        data-testid="kpi-empty"
      >
        <p className="text-sm text-kwanza-inkSoft">{t("kpi.empty", language)}</p>
      </section>
    );
  }

  const tiles: Tile[] = [
    {
      key: "invoices_processed",
      label: t("kpi.invoices_processed", language),
      value: String(view.invoices_processed),
      target: { status: "all" },
    },
    {
      key: "invoices_filed",
      label: t("kpi.invoices_filed", language),
      value: String(view.invoices_filed_count),
      target: { status: "filed" },
    },
    {
      key: "items_to_review",
      label: t("kpi.items_to_review", language),
      value: String(view.exceptions_raised_count),
      target: { status: "review" },
      highlight: view.exceptions_raised_count > 0,
    },
  ];

  const vendorCount = view.new_vendors_discovered.length;

  const activate = (target: InvoiceTarget): void => onTile?.(target);
  const onKeyDown = (e: KeyboardEvent, target: InvoiceTarget): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      activate(target);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {/* The tiles ARE the range-scoped surface — Needs-you and the activity
       * teaser are not. The window label lives here rather than in the page
       * header, where it read as governing every card below it (design
       * review finding 7). */}
      <p
        className="text-xs font-bold uppercase tracking-wide text-kwanza-inkSoft"
        data-testid="kpi-window-label"
      >
        {windowLabel}
      </p>
      <section
        className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        aria-label={windowLabel}
      >
        {tiles.map((tile) => (
          <div
            key={tile.key}
            role="button"
            tabIndex={0}
            onClick={() => activate(tile.target)}
            onKeyDown={(e) => onKeyDown(e, tile.target)}
            data-testid={`kpi-${tile.key}`}
            className={
              "cursor-pointer rounded-[2px] border bg-kwanza-card p-4 shadow-sm transition-colors hover:border-kwanza-accent " +
              (tile.highlight
                ? "border-kwanza-warnFill bg-kwanza-warnTint/50"
                : "border-kwanza-divider")
            }
          >
            <div
              className={
                "text-sm font-medium uppercase tracking-wide " +
                (tile.highlight ? "text-kwanza-warnText" : "text-kwanza-inkSoft")
              }
            >
              {tile.label}
            </div>
            <div
              className={
                "mt-2 text-3xl font-semibold " +
                (tile.highlight ? "text-kwanza-warnText" : "text-kwanza-ink")
              }
            >
              {tile.value}
            </div>
          </div>
        ))}
      </section>

      {/* Muted secondary line — the softer / estimated figures (Task 4). */}
      <p
        className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-kwanza-inkSoft"
        data-testid="kpi-secondary"
      >
        <span>
          {tn(
            "kpi.secondary.hours",
            { h: view.hours_saved.toFixed(1) },
            language,
          )}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          {tn(
            vendorCount === 1
              ? "kpi.secondary.vendors.one"
              : "kpi.secondary.vendors.other",
            { n: vendorCount },
            language,
          )}
        </span>
      </p>
    </div>
  );
}
